import { trackAnalyticsEvent } from '@cof/refi-web-sdk';
import { environment } from '../../../environment/environment.js';

const ENV_PATH = environment.isStaticCloud() ? '/_origin/dxgw/_call' : '';

const STIP_TASK_ID = {
  PROOF_OF_RESIDENCE: 4,
  PROOF_OF_INCOME: 21,
  TITLE: 38,
};

export const REFI_OFFERS_CONST = {
  get OFFERS_BFF_URL() {
    return `${ENV_PATH}/protected/948625/fs-offers-bff/applications/`;
  },
  get HOST_BFF_URL() {
    return `${ENV_PATH}/protected/831647/host-app/bff`;
  },
  GET_FEATURE_TOGGLE_URL: featureKey => `${ENV_PATH}/protected/948625/fs-offers-bff/features/${featureKey}`,
  BASE_APPLICATION_URL: `${ENV_PATH}/protected/offers/`,
  BASE_PREQUAL_URL: `${ENV_PATH}/protected/40748/`,
  IDP_DXGW_LOGOUT_URL: `${ENV_PATH}/protected/831647/host-app/bff/auth/logout`,
  HTTP_HEADERS: {
    Accept: 'application/json;v=2',
    'Content-Type': 'application/json;v=2',
  },
  C1_REFI_APPIDS: 'C1-REFI-APPIDS',
  MAX_INCOME: 300000,
  REFI_SESSION_KEY: 'auto-refi-session',
  REFI_CUSTOMER_EXPERIENCE_KEY: 'customer-experience',
  REFI_PI_KEY: 'auto-refi-pi',
  REFI_CI_KEY: 'refi-ci',
  LENDING_TREE_SRC_CD: 'SRLT03GENERICB***********',
  SELECTED_OFFER_EVENT: new CustomEvent('selected-offer', {
    bubbles: true,
    composed: true,
  }),
  TRACK_SELECTED_OFFER(offer) {
    document.dispatchEvent(
      new CustomEvent('selected-offer', {
        bubbles: true,
        composed: true,
        detail: offer,
      }),
    );
  },
  ROUTE_CHANGE: 'mfe-internal-route-change',
  ROUTE_CHANGE_EVENT: new CustomEvent('mfe-internal-route-change'),
  REVIEW_DISCONNECT_EVENT: new CustomEvent('review-disconnect'),
  // https://confluence.kdc.capitalone.com/display/MF/Task+ID+List
  STIP_TASK_ID,
  STIPS_TASK_IDS: [STIP_TASK_ID.PROOF_OF_RESIDENCE, STIP_TASK_ID.PROOF_OF_INCOME, STIP_TASK_ID.TITLE],
  VALID_ID_ANSWERS_EVENT: new CustomEvent('verified-id'),
  GO_TO_PREQUAL_EVENT: new CustomEvent('go-to-prequal'),
  TRACK_EVENT: trackAnalyticsEvent,
  TRACK_OFFER(offer) {
    document.dispatchEvent(
      new CustomEvent('refi-offer-card-shown', {
        bubbles: true,
        composed: true,
        detail: offer,
      }),
    );
  },
  TIMEOUTS: {
    TWO_MINUTES: 120000,
    FIVE_SEC: 5000,
  },
  PATTERNS: {
    ALPHANUMERIC: /^[0-9a-zA-Z]+$/,
    NUMBER_ONLY: /^[0-9]+$/,
    DELETE_KEYS: /(Backspace)|(Delete)/,
  },
  MAX_REFI_AGE: 7,
  API_ERR_MSG: 'Our system experienced an error. Please try again later. For help right now, you can call us at 1-888-263-4582.',
  EXISTING_HARDPULL_DECISION: 'Application has existing hardpull decision',
  API_DUPLICATE_HARDPULL_ERR_MSG:
    "This pre-qualification is no longer active. If you think you may have an active pre-qualification, please try closing your browser window and logging back in. If that doesn't work, please call us at 1-888-263-4582 Monday through Friday, from " +
    '9:00 a.m. to 7:00 p.m. Eastern Time.',
  REVIEW_ELLEN: {
    quote: `Capital One made it so easy for me to refinance my car. I knew my rate before there was a hard inquiry.`,
    author: `Ellen, TX`,
    comment: `Knowing your offers up front removes some of the uncertainty when refinancing.`,
  },
  REVIEW_ERIKA: {
    quote: `This auto refinance was amazing - so simple and painless; no additional or hidden fees, just straight up and very good. I can highly recommend it. I wouldn't know how to make it better!`,
    author: `Erika, OR`,
  },
  REVIEW_TONIA: {
    quote: `Excited for a lower interest rate through Capital One. Will make it more convenient to pay and check things through the easy access of the app.`,
    author: `Tonia, IN`,
  },
  REVIEW_PATRICK: {
    quote: `From the first soft inquiry pre-qualification to the final hard inquiry application, refinancing with Capital One has been an effective, secure way of getting my truck financed.`,
    author: `Patrick, MS`,
  },
  REVIEW_JOSEPH: {
    quote: `Filled out the application, submitted my current lender info, and Capital One took it from there! The loan was fulfilled in a matter of days.`,
    author: `Joseph, AR`,
    comment: `Once you eSign, we'll verify your information before paying off your lender.`,
  },
  REVIEW_DAVID: {
    quote: `Fast, no hassle, all online, and not much paperwork!`,
    author: `David, PA`,
    comment: `Once you eSign, we'll verify your information before paying off your lender.`,
  },
  REVIEW_EVAN: {
    quote: `Capital One was much more user friendly. It was really easy to switch, the app works great, customer service is helpful, and I have a credit card with them so I can see both balances in one place.`,
    author: `Evan, IN`,
    comment: `Get your loan details, pay your bill, and view additional capital one accounts with our app.`,
  },
  get isLocalEnv() {
    return window.location.hostname === 'localhost';
  },
  get isSBEnv() {
    return ['auto-refi-sb.clouddqt.capitalone.com', 'coaf-refi-offers-mfe-sb.clouddqtext.capitalone.com'].includes(window.location.hostname);
  },
  get isDevEnv() {
    return ['auto-refi-dev.clouddqt.capitalone.com', 'coaf-refi-offers-mfe-dev.clouddqtext.capitalone.com'].includes(window.location.hostname);
  },
  get isQAEnv() {
    return ['autorefi-ext-preprod.capitalone.com', 'coaf-refi-offers-mfe-qa.clouddqtext.capitalone.com'].includes(window.location.hostname);
  },
  get isStaticCloud() {
    return ['auto-refi-sb.clouddqt.capitalone.com', 'auto-refi-dev.clouddqt.capitalone.com', 'auto-refi-perf.clouddqt.capitalone.com'].includes(window.location.hostname);
  },
  get isProdEnv() {
    return ['autorefi.capitalone.com', 'coaf-refi-web-ui.capitalone.com', 'coaf-offers-mfe.capitalone.com'].includes(window.location.hostname);
  },
  UI_VARIANT: {
    compareToOldLoan: 'COMPARE_TO_OLD_LOAN',
  },
  MARKETING_ATTRIBUTE_COOKIE: 'afw3', // set in persist-marketing-attributes
  REFI_LOGO_URL: 'https://www.capitalone.com/auto-financing/refinance',
  COMPACT_COMPARE: 'compactcompare',
  NO_PAPERWORK_EXPERIENCE: 'nopaperwork',
  GOAL_HEADER_EXPERIENCE: 'goalheader',
  ADOBE_HOLDOUT_TAG: 'BAU_COMPARE_TO_OLD_LOAN',
  BANDIT_LOAD_EVENT: 'PageLoaded',
  BANDIT_DELIVERED_EVENT: 'Delivered',
  BANDIT_CLICKED_EVENT: 'Clicked',
  isAPR(apr) {
    return apr != null;
  },
  BUILD_YOUR_OFFERS_DISCLOSURE: 'Your monthly payment savings are calculated based on the info we have about your current loan. If that info changes, your monthly payment and savings could change.',
  OFFERS_TYPE: {
    postApp: 'offers',
  },
  PAGE_NAMES: {
    OFFER: 'offers',
    BUILD_YOUR_OFFER: 'build-your-offers',
    REVIEW_OFFER_CARD: 'review-offer',
    APPROVED: 'approved',
  },
  TIMEOUT_IN_MS: 10000,
};

export const FEATURE_TOGGLES = {
  SKELETON_LOADER: 'offers-skeleton-load',
  ICONIC_OFFER: 'iconic-offer',
  CXT_ICONIC_OFFERS: 'cxt-iconic-offers',
};
